import {
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Add, Block, Save } from "@material-ui/icons";
import CommonButton from "app/common/Component/Button/CommonButton";
import PopupForm from "app/common/Component/Popup/PopupForm";
import FormikFocusError from "app/common/form/FormikFocusError";
import GlobitsDateTimePicker from "app/common/form/GlobitsDateTimePicker";
import GlobitsNumberInput from "app/common/form/GlobitsNumberInput";
import GlobitsPagingAutocomplete from "app/common/form/GlobitsPagingAutocomplete";
import GlobitsTextField from "app/common/form/GlobitsTextField";
import GlobitsVNDCurrencyInput from "app/common/form/GlobitsVNDCurrencyInput";
import IconDelete from "app/common/Icon/IconDelete";
import { formatMoney, transformDate } from "app/common/LocalFunction";
import { pagingStaff } from "app/services/StaffService";
import { FieldArray, getIn, useFormikContext } from "formik";
import { observer } from "mobx-react";
import React, { forwardRef, memo, useEffect, useState } from "react";

import GlobitsAutocompleteTextAndSelect from "app/common/form/GlobitsAutocompleteTextAndSelect";
import * as Yup from "yup";
import { useStore } from "app/stores";
import { pagingMedicalSupply } from "app/services/MedicalSupplyService";
import { pagingQuantityUnit } from "app/services/QuantityUnitService";
import { searchItemByPage } from "app/services/ScreenTemplateService";
import ScreenTemplatePopup from "app/common/Component/Popup/ScreenTemplatePopup";
import RowTotalService from "../Component/RowTotalService";
import RowTotalProduct from "../Component/RowTotalProduct";
import GlobitsPagingAutocompleteV2 from "app/common/form/GlobitsPagingAutocompleteV2";
import { pagingPosition } from "app/services/PositionService";

const RegisterMedicalSupplyPopup = ({
  open,
  handleClose,
  onSubmit,
  initRegisterMedicalSupply,
  listVatPercent,
  resetPopupStore,
}) => {
  const validationSchema = Yup.object().shape({
    orderDate: Yup.date()
      .transform(transformDate)
      .typeError("Ngày đăng ký chưa đúng định dạng!")
      .nullable()
      .required("Chưa nhập ngày đăng ký"),
    soldQuantity: Yup.number().nullable().required("Chưa nhập số lượng!"),
    price: Yup.number().nullable().required("Chưa nhập giá!"),
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const { handleGetListVatPercent } =
    useStore().patientManagementStore.patientGeneralStore;

  useEffect(() => {
    handleGetListVatPercent();
    return () => resetPopupStore();
  }, []);
  return (
    <PopupForm
      open={open}
      size="xl"
      handleClose={handleClose}
      title={`${initRegisterMedicalSupply?.id ? "Chỉnh sửa" : "Thêm"}  vật tư`}
      formik={{
        initialValues: initRegisterMedicalSupply,
        onSubmit: onSubmit,
        enableReinitialize: true,
        validationSchema: validationSchema,
      }}
      action={({ isSubmitting }) => {
        return (
          <div className="flex-center gap-1">
            <CommonButton
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {!isMobile && <Save className="mr-2 " />}
              {`${initRegisterMedicalSupply?.id ? "Chỉnh sửa" : "Thêm"} vật tư`}
            </CommonButton>

            <CommonButton className="btn-gray" onClick={handleClose}>
              <Block className="mr-2" /> Đóng
            </CommonButton>
          </div>
        );
      }}
    >
      {({ setFieldValue, values }) => {
        return (
          <>
            <ValueForm listVatPercent={listVatPercent} />
          </>
        );
      }}
    </PopupForm>
  );
};

const ValueForm = observer(({ listVatPercent }) => {
  const { setFieldValue, values } = useFormikContext();
  const [popupData, setPopupData] = useState(null);
  return (
    <>
      <FormikFocusError />
      <Grid container spacing={1}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={2}>
              <GlobitsDateTimePicker
                name={"orderDate"}
                label="Ngày đăng ký"
                maxDateMessage="Ngày đăng ký phải trước ngày hiện tại"
                maxDate={new Date()}
                isDateTimePicker
                required
              />
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <GlobitsPagingAutocomplete
                label="Vật tư y tế"
                api={pagingMedicalSupply}
                name={`medicalSupply`}
                getOptionLabel={(medical) => {
                  if (!medical) return "";
                  const code = medical?.code || "";
                  const name = medical?.productName || medical?.name || "";
                  return `${code} - ${name}`;
                }}
                onChange={(_, val) => {
                  setFieldValue(`medicalSupply`, val);
                  setFieldValue("price", val?.price);
                  setFieldValue(
                    "totalAmount",
                    values?.soldQuantity * val?.price
                  );

                  setFieldValue("drug", null);

                  let revenueAllocations = val?.revenueAllocations;
                  if (revenueAllocations) {
                    let staffRevenues = revenueAllocations?.map((el) => ({
                      staff: null,
                      position: el?.position,
                      percentRevenue: el?.commissionRate,
                    }));
                    setFieldValue("staffRevenues", staffRevenues);
                  }
                  else {
                    setFieldValue("staffRevenues", []);
                  }
                }}
              />
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
              md={3}
              className="flex align-end space-x-2"
            >
              <GlobitsPagingAutocomplete
                label={"Cách dùng"}
                api={searchItemByPage}
                name={"howToUse"}
                freeSolo
                displayValue="value"
                displayLabel="value"
                displaySelected="value"
              />
              <Tooltip title="Thêm mẫu cách dùng">
                <IconButton
                  size="small"
                  className="btn-orange ml-1"
                  onClick={() =>
                    setPopupData({
                      open: true,
                      // clientCode: "[OUTERNAL_EXAM]howToUse",
                      // screenCode: "[OUTERNAL_EXAM]",
                      title: "Cách dùng thuốc",
                    })
                  }
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <GlobitsNumberInput
                label={"Số lượng"}
                name={"soldQuantity"}
                onChange={(e) => {
                  let soldQuantity = +e.target.value;
                  setFieldValue("soldQuantity", soldQuantity);
                  if (values?.price) {
                    setFieldValue("totalAmount", soldQuantity * values?.price);
                  }
                }}
                required
              />
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <GlobitsPagingAutocomplete
                label={"Đơn vị tính"}
                api={pagingQuantityUnit}
                name={"usedUnit"}
              />
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <GlobitsVNDCurrencyInput
                label={"Giá"}
                name={"price"}
                onChange={(e) => {
                  let price = e.target.value;
                  setFieldValue("price", price);
                  setFieldValue("totalAmount", values?.soldQuantity * price);
                }}
                required
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <GlobitsVNDCurrencyInput
                label={"Thành tiền"}
                name={"totalAmount"}
                required
              />
            </Grid>

            <Grid item xs={6} sm={6} md={2}>
              <GlobitsNumberInput
                label={"% giảm giá"}
                name={"percentDiscount"}
                onChange={(e) => {
                  let percentDiscount = +e?.target.value;
                  setFieldValue("percentDiscount", percentDiscount);
                  setFieldValue(
                    "discount",
                    (values?.price * values?.soldQuantity * percentDiscount) /
                    100
                  );
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <GlobitsVNDCurrencyInput
                label={"Số tiền giảm"}
                name={"discount"}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <GlobitsAutocompleteTextAndSelect
                options={listVatPercent || []}
                label={"% Vat"}
                name={"taxPercent"}
                value={values?.taxPercent}
                onInputChange={(newValue) => {
                  setFieldValue("taxPercent", newValue?.toString());
                  setFieldValue(
                    "taxTotal",
                    (values?.price * values?.soldQuantity - values?.discount) *
                    (+newValue / 100)
                  );
                }}
                displayLabel={"vatPercent"}
                onChange={(value) => {
                  setFieldValue("taxPercent", value?.vatPercent?.toString());
                  setFieldValue(
                    "taxTotal",
                    (values?.price * values?.soldQuantity - values?.discount) *
                    (+value?.vatPercent / 100)
                  );
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <GlobitsVNDCurrencyInput label={"Tiền Vat"} name={`taxTotal`} />
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <GlobitsTextField
                label={"Mã giảm giá"}
                name={"marketingVoucherCode"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GlobitsTextField label={"Ghi chú"} name={"note"} isTextArea />
            </Grid>
            <FieldArray name="staffRevenues">
              {({ remove, push }) => (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <GlobitsPagingAutocomplete
                      disableClearable
                      label={"Chọn nhân viên được phân bổ doanh thu"}
                      api={pagingStaff}
                      name={`staff`}
                      displayLabel="staffName"
                      autoUpercase
                      onChange={(_, value, reason, option) => {
                        push({ staff: value, percentRevenue: 100 });
                        if (reason === "remove-option") {
                          return;
                        }
                      }}
                      renderTags={() => ""}
                      value={values?.staff || []}
                      getOptionDisabled={(option) => {
                        const selectedService = getIn(values, "staffRevenues");
                        if (
                          selectedService?.find(
                            (it) => it?.staff?.id === option?.id
                          )
                        ) {
                          return true;
                        }
                        return false;
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <div className="table-root mt-2" style={{ width: "100%" }}>
                      <table className="table-container">
                        <thead>
                          <tr className="row-table-header">
                            <th width="5%">STT</th>
                            <th width="35%">Chức danh</th>
                            <th width="40%">Nhân viên</th>
                            <th width="20%">Phân bổ doanh thu</th>

                            <th>Thao tác</th>
                          </tr>
                        </thead>
                        <tbody>
                          {values?.staffRevenues?.length > 0 &&
                            values?.staffRevenues?.map((item, index) => {
                              let positionName = values?.staffRevenues[index]?.position?.name;
                              let staffName = values?.staffRevenues[index]?.staff?.staffName;

                              if (!positionName) {
                                positionName = "Chưa lựa chọn";
                              }
                              if (!staffName) {
                                staffName = "Chưa lựa chọn";
                              }

                              return (
                                <tr key={index}>
                                  <td align="center">{index + 1}</td>
                                  <td>
                                    <Tooltip
                                      arrow
                                      placement="top"
                                      title={positionName}
                                    >
                                      {/* <GlobitsAutocomplete
                                                                                         options={values?.service?.revenueAllocations?.map(
                                                                                             (el) => el?.position
                                                                                         )}
                                                                                         name={`staffRevenues[${index}].position`}
                                                                                         autoUpercase
                                                                                     /> */}

                                      <GlobitsPagingAutocompleteV2
                                        api={pagingPosition}
                                        searchObject={{
                                          isUsing: true
                                        }}
                                        name={`staffRevenues[${index}].position`}
                                        autoUpercase
                                        handleChange={function (_, value) {
                                          setFieldValue(`staffRevenues[${index}].position`, value);
                                          setFieldValue(`staffRevenues[${index}].staff`, null);
                                        }}
                                      />
                                    </Tooltip>

                                  </td>
                                  <td>
                                    <Tooltip
                                      arrow
                                      placement="top"
                                      title={staffName}
                                    >
                                      <GlobitsPagingAutocompleteV2
                                        api={pagingStaff}

                                        name={`staffRevenues[${index}].staff`}
                                        displayName="staffName"
                                        autoUpercase
                                        searchObject={{
                                          positionId: values?.staffRevenues[index]?.position?.id
                                        }}
                                      />
                                    </Tooltip>
                                  </td>
                                  <td>
                                    <GlobitsNumberInput
                                      name={`staffRevenues[${index}].percentRevenue`}
                                    />
                                  </td>

                                  <td align="center">
                                    <span
                                      tooltip={"Xóa"}
                                      className="pointer tooltip"
                                      onClick={() => remove(index)}
                                    >
                                      <IconDelete />
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </Grid>
                </>
              )}
            </FieldArray>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <RowTotalProduct data={values} />
        </Grid>
        {popupData?.open && (
          <ScreenTemplatePopup
            openValue={popupData}
            handleClose={() => setPopupData(null)}
            hideSelectCheckbox={true}
          />
        )}
      </Grid>
    </>
  );
});

const renderOptionsMedicalSupply = (option) => {
  return (
    <>
      <p data-tooltip={option?.code}>
        <b>{option?.code}</b>
      </p>
      <p data-tooltip={option?.name}>
        <b>{option?.name}</b>
      </p>
      <p data-tooltip={option?.groupName}>
        <b>{option?.groupName || "N/A"}</b>
      </p>
      <p>
        <span
          style={{
            fontSize: "10px",
            borderRadius: "5px",
            backgroundColor: "#a8e1a8",
            padding: "2px",
            width: "50%",
          }}
        >
          <strong>{formatMoney(option?.price || 0)}</strong>
        </span>
      </p>
    </>
  );
};

export default memo(observer(RegisterMedicalSupplyPopup));
